import { gql } from "lib/apolloClient";
import {
  organizationResourceParentFragment,
  questionFragments,
} from "Worksheet/modules/WorksheetFragments";
import { resolvedFieldValueFragment } from "modules/CommonFragments";
import {
  unitPlanFragment,
  assessmentFragment,
  leCollectionFragment,
  libraryFragments,
  entitySavedByNodeFragment,
  organizationResourceQuestionFragment,
  organizationResourceQuestionFeedFragment,
  assessmentTitleFragment,
  associatedEntityTypesFragments,
  questionDirectFeedFragmentV2,
  question,
} from "./ToddleLibraryFragments";

export const getToddleLibraryGroupFeedQuery = gql`
  query getToddleLibraryGroupFeed(
    $id: ID!
    $seed: Float!
    $filters: LibraryEntityFilter
    $feedType: LIBRARY_FEED_ENUM
    $collectionFilterInput: collectionFilterInput
    $showOnlyAccessible: Boolean
  ) {
    node(id: $id, type: STAFF) {
      id
      ... on Staff {
        id
        libraryLanes(
          seed: $seed
          filters: $filters
          feedType: $feedType
          showOnlyAccessible: $showOnlyAccessible
        ) {
          label
          metaData {
            laneType
            laneOrder
            entityType
          }
          entities {
            totalCount
            edges {
              isPremium
              isNew
              node {
                id
                ... on UnitPlan {
                  id
                  ...unitPlanItem
                }
                ... on LECollection {
                  id
                  ...leCollectionItem
                }
                ... on Assessment {
                  id
                  ...assessmentItem
                }
                ... on MockExam {
                  id
                  label
                  isSavedForLibrary
                  worksheet {
                    totalCount
                    edges {
                      node {
                        id
                        ...assessmentItem
                      }
                    }
                  }
                }
                ... on OrganizationResource {
                  id
                  ...organizationResourceQuestionFeedItem
                }
                ... on LibraryTopic {
                  id
                  image {
                    id
                    url
                  }
                  label
                }
              }
            }
          }
        }
      }
    }
  }
  ${unitPlanFragment.unitPlanFeed}
  ${leCollectionFragment.leCollectionFeed}
  ${assessmentFragment.assessmentFeed}
  ${organizationResourceQuestionFeedFragment}
`;

export const getLibraryCommonFeedQuery = gql`
  query getLibraryCommonFeed(
    $id: ID!
    $entityTypes: [LIBRARY_ENTITY_TYPE_ENUM!]
    $searchText: String
    $first: Int
    $after: String
    $feedType: LIBRARY_FEED_ENUM
    $filters: LibraryEntityFilter
    $seed: Float!
    $sortBy: LIBRARY_SORT_ENUM
    $isPaginated: Boolean!
    $showOnlyAccessible: Boolean
    $questionFilters: OrganizationResourceQuestionItemFilters
    $worksheetFilters: LibraryWorksheetFilters
    $collectionFilterInput: collectionFilterInput
    $contextCurriculumProgramId: NumericId
  ) {
    node(id: $id, type: STAFF) {
      id
      ... on Staff {
        id
        libraryEntities(
          entityTypes: $entityTypes
          first: $first
          after: $after
          searchText: $searchText
          feedType: $feedType
          filters: $filters
          seed: $seed
          sortBy: $sortBy
          showOnlyAccessible: $showOnlyAccessible
          questionFilters: $questionFilters
          worksheetFilters: $worksheetFilters
          contextCurriculumProgramId: $contextCurriculumProgramId
        ) {
          totalCount
          categorisedCount {
            le
            unitPlan
            leCollection
            question
            worksheet
            mockExam
            eAssessment
          }
          edges {
            isPremium
            isNew
            node {
              id
              ... on UnitPlan {
                id
                ...unitPlanItem
              }
              ... on Assessment {
                id
                ...assessmentItem
              }
              ... on LECollection {
                id
                ...leCollectionItem
              }
              ... on MockExam {
                id
                label
                isSavedForLibrary
                worksheet {
                  totalCount
                  edges {
                    node {
                      id
                      ...assessmentItem
                    }
                  }
                }
              }
              ... on OrganizationResource {
                id
                ...organizationResourceQuestionFeedItem
              }
            }
          }
          pageInfo @include(if: $isPaginated) {
            hasNextPage
            endCursor
          }
        }
      }
    }
  }
  ${unitPlanFragment.unitPlanFeed}
  ${assessmentFragment.assessmentFeed}
  ${leCollectionFragment.leCollectionFeed}
  ${organizationResourceQuestionFeedFragment}
`;

export const getCollectionQuery = gql`
  query getCollection($id: ID!, $collectionFilterInput: collectionFilterInput) {
    node(id: $id, type: LE_COLLECTION) {
      id
      ... on LECollection {
        id
        name
        description
        ...leCollectionSavedByNodeItem
        libraryGrades {
          id
          label
        }
        entities(entityType: ASSESSMENT, filters: $collectionFilterInput) {
          totalCount
          libraryEdges {
            isPremium
            isNew
            node {
              id
              ... on Assessment {
                id
                ...assessmentItem
              }
            }
          }
        }
      }
    }
  }
  ${assessmentFragment.assessmentFeed}
  ${entitySavedByNodeFragment.leCollection}
`;

export const getCollectionGradesQuery = gql`
  query getCollection($id: ID!) {
    node(id: $id, type: LE_COLLECTION) {
      id
      ... on LECollection {
        id
        libraryGrades {
          id
          label
        }
      }
    }
  }
`;

export const getLibraryConstantsQuery = gql`
  query getLibraryConstant {
    library {
      constants {
        unitTypesV2 {
          id
          label
        }
        libraryTopics {
          id
          label
          libraryEntity {
            id
            label
            type
          }
          image {
            id
            url
          }
        }
      }
    }
  }
`;

export const getStaffGradesQuery = gql`
  query getLibraryStaffGrades($id: ID!) {
    node(id: $id, type: STAFF) {
      id
      ... on Staff {
        id
        libraryEntities(entityTypes: LIBRARY_GRADE) {
          edges {
            node {
              ... on LibraryGenericEntity {
                id
                label
                type
              }
            }
          }
        }
      }
    }
  }
`;

export const getLibraryGradesQuery = gql`
  query getOrganizationDetails($id: ID!) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        name
        libraryGrades {
          ...libraryEntityBasicDetailsItem
          mappedLibraryEntities(filters: { types: [SUBJECT] }) {
            ...libraryEntityBasicSubjectDetailsItem
            mappedOrganizationEntities {
              ... on Subject {
                id
                name
                curriculumProgram {
                  id
                  type
                }
              }
            }
          }
          mappedOrganizationEntities {
            ... on Grade {
              id
              globalGrade {
                id
                uid
              }
            }
          }
        }
      }
    }
  }
  ${libraryFragments.libraryEntityBasicDetails}
  ${libraryFragments.libraryEntityBasicSubjectDetailsItem}
`;

export const getLibrarySearchSuggestionsQuery = gql`
  query getLibrarySearchSuggestions(
    $filters: LibrarySearchSuggestionsFilterInput!
  ) {
    library {
      searchSuggestions(filters: $filters)
    }
  }
`;

export const getOrganizationResourceQuery = gql`
  query getOrganizationResource(
    $id: ID!
    $libraryBannerFilters: LibraryPremiumBannerFilters
  ) {
    node(id: $id, type: ORGANIZATION_RESOURCE) {
      id
      ... on OrganizationResource {
        ...organizationResourceQuestionItem
      }
    }
  }
  ${organizationResourceQuestionFragment}
`;

export const getSubjectTopicsQuery = gql`
  query getSubjectTopics($ids: [ID!], $depth: Int) {
    multiNode(ids: $ids, type: SUBJECT) {
      id
      ... on Subject {
        id
        name
        associatedChildPlannerElementNodes(
          filters: { types: DP_SYLLABUS, depth: $depth }
        ) {
          id
          label
          displaySequence
          parent
        }
      }
    }
  }
`;

export const getAssessmentDetailsQuery = gql`
  query getAssessmentDetails(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $orgResourceFilters: OrganizationResourceFilter
  ) {
    node(id: $id, type: $type) {
      ... on Assessment {
        id
        organizationResourcesV2(first: null, filters: $orgResourceFilters) {
          edges {
            node {
              childrenV2 {
                edges {
                  node {
                    id
                    item {
                      id
                    }
                    childrenV2 {
                      edges {
                        node {
                          id
                          item {
                            id
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
        title {
          id
          uid
          value
        }
      }
    }
  }
`;

export const getCourseDetailsQuery = gql`
  query getCourseDetails($id: ID!, $type: ENTITY_TYPE_ENUM!) {
    node(id: $id, type: $type) {
      id
      ... on Course {
        id
        primaryGrade {
          id
          plannerTemplates(filters: { templateType: [UNIT_PLAN] }) {
            id
            name
            unitType
            type
            subType
            curriculum {
              id
            }
          }
        }
      }
      ... on LearningCourse {
        id
        primaryGrade {
          id
          plannerTemplates(filters: { templateType: [UNIT_PLAN] }) {
            id
            name
            unitType
            type
            subType
            curriculum {
              id
            }
          }
        }
      }
    }
  }
`;

/**
 * @deprecated use getAssessmentQuestionsForLibraryQueryV2 instead
 * This is kept for backward compatibility
 * It will be removed in future versions
 */
export const getAssessmentQuestionsForLibraryQuery = gql`
  query getAssessmentQuestionsForLibrary(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $orgResourceFilters: OrganizationResourceFilter
    $libraryBannerFilters: LibraryPremiumBannerFilters
  ) {
    node(id: $id, type: $type) {
      ... on Assessment {
        id
        organizationResourcesV2(first: null, filters: $orgResourceFilters) {
          edges {
            node {
              ...organizationResourceParentItem
            }
          }
        }
        ...assessmentTitle
        libraryGrades {
          ...libraryEntityBasicDetailsItem
        }
        librarySubjects {
          ...libraryEntityBasicDetailsItem
        }
        allFields {
          id
          uid
          value
          resolvedMinimalTree {
            ...resolvedMinimalTreeItem
          }
        }
        isLibraryPremium
        showLibraryPremiumBannerForUser(filters: $libraryBannerFilters)
        ...assessmentSavedByNodeItem
      }
    }
  }
  ${entitySavedByNodeFragment.assessment}
  ${organizationResourceParentFragment}
  ${libraryFragments.libraryEntityBasicDetails}
  ${resolvedFieldValueFragment.resolvedMinimalTree}
  ${assessmentTitleFragment}
`;

export const getMypPlannerElementSetsQuery = gql`
  query getMypPlannerElementSetsQuery(
    $id: ID!
    $elementTypes: [PLANNER_ELEMENT_TYPE_ENUM]
  ) {
    node(id: $id, type: IB_MYP_CURRICULUM_PROGRAM) {
      id
      ... on IbMypCurriculumProgram {
        id
        plannerElementSets(elementTypes: $elementTypes) {
          nodes {
            id
            ...plannerElementNodeItemV2
          }
        }
      }
    }
  }
  ${associatedEntityTypesFragments.plannerElementNode}
`;

export const getMypCriterionQuery = gql`
  query getMypCriterion($curriculumProgramId: ID!) {
    node(id: $curriculumProgramId, type: IB_MYP_CURRICULUM_PROGRAM) {
      id
      ... on IbMypCurriculumProgram {
        id
        plannerElementSets(elementTypes: [MYP_LEARNING_OBJECTIVES]) {
          id
          nodes {
            id
            ...plannerElementNodeBasicItem
          }
        }
      }
    }
  }
  ${associatedEntityTypesFragments.plannerElementNodeBasic}
`;

/**
 * V2 version of getToddleLibraryGroupFeedQuery.
 */
export const getToddleLibraryGroupFeedQueryV2 = gql`
  query getToddleLibraryGroupFeedV2(
    $id: ID!
    $seed: Float!
    $filters: LibraryEntityFilter
    $feedType: LIBRARY_FEED_ENUM
    $collectionFilterInput: collectionFilterInput
    $showOnlyAccessible: Boolean
  ) {
    node(id: $id, type: STAFF) {
      id
      ... on Staff {
        id
        libraryLanes(
          seed: $seed
          filters: $filters
          feedType: $feedType
          showOnlyAccessible: $showOnlyAccessible
        ) {
          label
          metaData {
            laneType
            laneOrder
            entityType
          }
          entities {
            totalCount
            edges {
              isPremium
              isNew
              node {
                id
                ... on UnitPlan {
                  id
                  ...unitPlanItem
                }
                ... on LECollection {
                  id
                  ...leCollectionItem
                }
                ... on Assessment {
                  id
                  ...assessmentItem
                }
                ... on MockExam {
                  id
                  label
                  isSavedForLibrary
                  worksheet {
                    totalCount
                    edges {
                      node {
                        id
                        ...assessmentItem
                      }
                    }
                  }
                }
                ... on LibraryTopic {
                  id
                  label
                  image {
                    id
                    url
                  }
                }
                ... on Question {
                  id
                  ...questionDirectFeedItemV2
                }
              }
            }
          }
        }
      }
    }
  }
  ${unitPlanFragment.unitPlanFeed}
  ${leCollectionFragment.leCollectionFeed}
  ${assessmentFragment.assessmentFeedV2}
  ${questionDirectFeedFragmentV2}
`;

export const getAssessmentDetailsQueryV2 = gql`
  query getAssessmentDetailsV2($id: ID!, $type: ENTITY_TYPE_ENUM!) {
    node(id: $id, type: $type) {
      ... on Assessment {
        id
        questions {
          totalCount
          edges {
            id
            parentIdV2
            sequence
            parentTypeV2
            questionType {
              id
              uid
            }
          }
        }
        title {
          id
          uid
          value
        }
      }
    }
  }
`;

export const getLibraryCommonFeedQueryV2 = gql`
  query getLibraryCommonFeedV2(
    $id: ID!
    $entityTypes: [LIBRARY_ENTITY_TYPE_ENUM!]
    $searchText: String
    $first: Int
    $after: String
    $feedType: LIBRARY_FEED_ENUM
    $filters: LibraryEntityFilter
    $seed: Float!
    $sortBy: LIBRARY_SORT_ENUM
    $isPaginated: Boolean!
    $showOnlyAccessible: Boolean
    $questionFilters: OrganizationResourceQuestionItemFilters
    $worksheetFilters: LibraryWorksheetFilters
    $collectionFilterInput: collectionFilterInput
    $contextCurriculumProgramId: NumericId
  ) {
    node(id: $id, type: STAFF) {
      id
      ... on Staff {
        id
        libraryEntities(
          entityTypes: $entityTypes
          first: $first
          after: $after
          searchText: $searchText
          feedType: $feedType
          filters: $filters
          seed: $seed
          sortBy: $sortBy
          showOnlyAccessible: $showOnlyAccessible
          questionFilters: $questionFilters
          worksheetFilters: $worksheetFilters
          contextCurriculumProgramId: $contextCurriculumProgramId
        ) {
          totalCount
          categorisedCount {
            le
            unitPlan
            leCollection
            question
            worksheet
            mockExam
            eAssessment
            questionV2
          }
          edges {
            isPremium
            isNew
            node {
              id
              ... on UnitPlan {
                id
                ...unitPlanItem
              }
              ... on Assessment {
                id
                ...assessmentItem
              }
              ... on LECollection {
                id
                ...leCollectionItem
              }
              ... on MockExam {
                id
                label
                isSavedForLibrary
                worksheet {
                  totalCount
                  edges {
                    node {
                      id
                      ...assessmentItem
                    }
                  }
                }
              }
              ... on Question {
                id
                ...questionDirectFeedItemV2
              }
            }
          }
          pageInfo @include(if: $isPaginated) {
            hasNextPage
            endCursor
          }
        }
      }
    }
  }
  ${unitPlanFragment.unitPlanFeed}
  ${assessmentFragment.assessmentFeedV2}
  ${leCollectionFragment.leCollectionFeed}
  ${questionDirectFeedFragmentV2}
`;

export const getLibraryQuestionQueryV2 = gql`
  query getLibraryQuestionV2(
    $id: ID!
    $libraryBannerFilters: LibraryPremiumBannerFilters
  ) {
    node(id: $id, type: QUESTION) {
      id
      ... on Question {
        isSavedForLibrary
        isLibraryPremium
        showLibraryPremiumBannerForUser(filters: $libraryBannerFilters)
        ...questionItem
        children {
          edges {
            ...questionItem
          }
        }
      }
    }
  }
  ${question}
`;

export const getAssessmentQuestionsForLibraryQueryV2 = gql`
  query getAssessmentQuestionsForLibraryV2(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $libraryBannerFilters: LibraryPremiumBannerFilters
  ) {
    node(id: $id, type: $type) {
      ... on Assessment {
        id
        questions {
          totalCount
          edges {
            ...questionItem
          }
        }
        ...assessmentTitle
        libraryGrades {
          ...libraryEntityBasicDetailsItem
        }
        librarySubjects {
          ...libraryEntityBasicDetailsItem
        }
        allFields {
          id
          uid
          value
          resolvedMinimalTree {
            ...resolvedMinimalTreeItem
          }
        }
        isLibraryPremium
        showLibraryPremiumBannerForUser(filters: $libraryBannerFilters)
        ...assessmentSavedByNodeItem
      }
    }
  }
  ${questionFragments.questionV2}
  ${assessmentTitleFragment}
  ${libraryFragments.libraryEntityBasicDetails}
  ${resolvedFieldValueFragment.resolvedMinimalTree}
  ${entitySavedByNodeFragment.assessment}
`;

export const getUnitPlanCurriculumProgramQuery = gql`
  query getUnitPlanCurriculumProgram($id: ID!) {
    node(id: $id, type: UNIT_PLAN) {
      ... on UnitPlan {
        id
        curriculumProgram {
          id
          type
        }
      }
    }
  }
`;
